import { POST_LIMITS } from '@tomato/shared';
import { useRef, useState } from 'react';

import { ApiError } from '../api/client';
import { Spinner } from './ui';

interface UploadResponse {
  path: string;
  url: string;
}

/**
 * 이미지 첨부. 업로드는 글 저장과 분리돼 있다 —
 * 작성 중에는 글이 아직 없어서 post_images에 붙일 대상이 없기 때문이다.
 * 여기서는 저장 경로만 받아두고, 글을 저장할 때 imagePaths로 함께 보낸다.
 */
export function ImageUploader({
  paths,
  onChange,
  disabled,
  max = POST_LIMITS.maxImages,
  hint,
}: {
  paths: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** 몇 장까지. 글은 5장, 식단표는 1장처럼 쓰는 곳마다 다르다. */
  max?: number;
  /** 아래 안내 문구. 기본은 글 첨부 기준이라 다른 용도면 바꿔 넣는다. */
  hint?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const full = paths.length >= max;

  async function upload(files: FileList) {
    setError('');
    const room = max - paths.length;
    const picked = Array.from(files).slice(0, room);
    if (picked.length === 0) return;

    setBusy(true);
    const added: string[] = [];
    try {
      for (const file of picked) {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch('/api/uploads', {
          method: 'POST',
          credentials: 'same-origin',
          body: form,
        });
        const body: unknown = await res.json().catch(() => null);
        if (!res.ok) {
          const err = (body as { error?: { code?: string; message?: string } } | null)?.error;
          throw new ApiError(err?.code ?? 'unknown', err?.message ?? '올리지 못했어요.', res.status);
        }
        added.push((body as UploadResponse).path);
      }
      onChange([...paths, ...added]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '올리지 못했어요.');
      // 일부라도 성공했으면 반영한다. 다시 올리게 하면 중복만 쌓인다.
      if (added.length) onChange([...paths, ...added]);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {paths.map((path, i) => (
          <div key={path} className="group relative size-20 overflow-hidden rounded-xl bg-ink-50">
            <img src={`/api/images/${path}`} alt="" className="size-full object-cover" />
            {i === 0 && max > 1 && (
              <span className="absolute inset-x-0 bottom-0 bg-black/50 py-0.5 text-center text-[10px] text-white">
                대표
              </span>
            )}
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(paths.filter((p) => p !== path))}
              className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-black/55 text-[11px] leading-none text-white transition hover:bg-black/75"
              aria-label="이미지 삭제"
            >
              ×
            </button>
          </div>
        ))}

        {!full && (
          <button
            type="button"
            disabled={disabled || busy}
            onClick={() => input.current?.click()}
            className="flex size-20 flex-col items-center justify-center gap-1 rounded-xl bg-ink-50 text-ink-400 transition hairline hover:bg-ink-100 disabled:opacity-50"
          >
            {busy ? <Spinner /> : <span className="text-lg leading-none">＋</span>}
            {max > 1 && (
              <span className="text-[11px]">
                {paths.length}/{max}
              </span>
            )}
          </button>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        hidden
        onChange={(e) => e.target.files && void upload(e.target.files)}
      />

      <p className="mt-2 text-[12px] text-ink-400">
        {hint ?? 'JPG·PNG·GIF·WebP, 장당 5MB까지. 첫 장이 목록의 대표 이미지가 돼요.'}
      </p>
      {error && <p className="mt-1 text-[12px] text-tomato-600">{error}</p>}
    </div>
  );
}
